import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { FAQSchema, BreadcrumbSchema } from '../components/StructuredData';
const faqs = [{
  q: 'Where is your service area?',
  a: "Our expert maids are available in Bicester and Oxfordshire. However, we are expanding our team and the area we cover every day so if you are not sure try booking online or call us. We'll confirm in seconds."
}, {
  q: 'How to book a home or commercial cleaning?',
  a: "If you're online, simply select the service you want and click the Book Online button to get personalized pricing, schedule an appointment in real-time based on our actual availability, and receive a confirmation. It only takes 60 seconds. Use your smartphone, computer, or tablet. Alternatively, call us at 07432285564 or email us at smithsandjengs@yahoo.com."
}, {
  q: 'Do I need to be at home when you arrive for the cleaning?',
  a: ''
}, {
  q: 'Do I need to supply the vacuum or cleaning products?',
  a: "We come equipped with everything we need to make your home or commercial facility sparkle. But if you'd prefer that we use your cleaning products, just let us know when you book online or by phone."
}, {
  q: 'Do you use eco-friendly cleaning products?',
  a: "Yes, we do! Part of our commitment as a member of the community is not to harm the environment. That's why we use only eco-friendly products which do just as good a cleaning job as traditional chemical cleaning products. If you prefer that we use traditional chemical products, just let us know when you book."
}, {
  q: 'Do you clean homes that have pets?',
  a: 'Yes, we do! When you set up your online booking, please let us know the name and species of your pet and anything we need to know to make sure our housekeeper know what to do while she or he is in your house.'
}, {
  q: 'Will I receive reminders for single, recurring, or pre-paid bundle cleaning services?',
  a: 'When you book online or call us, you can select to receive a text message or email before each scheduled service. We typically send reminders one day and two hours before each cleaning appointment.'
}, {
  q: "What if I'm not happy with the work you do for me?",
  a: "Our reputation is our most valuable asset, so we offer a 100% satisfaction guarantee. If you're not satisfied, please contact us and we'll issue a full refund or make it right with a do-over."
}, {
  q: 'How long will it take to clean my home or commercial facility?',
  a: "Our time on-site will be based on the level of cleaning you select and the extras (if any) you order. We ensure and guarantee the quality of our service. If you aren't satisfied, we'll come back and make sure to address your concerns. 99% of our customers are repeat customers."
}, {
  q: 'How do I re-schedule or cancel a booked cleaning?',
  a: "Rescheduling or cancelling is one click away. Or simply call us or email us and we'll update your booking. It just takes a few seconds and we'll send you an updated booking confirmation. Our email service reminders can include a Reschedule and/or Cancel button which allows you to instantly reschedule or cancel your booking within pre-established periods prior to each service."
}, {
  q: 'Can I set up repeat visits?',
  a: "Yes, and you'll save when you do. For each of our services you can select and schedule either recurring appointments or purchase pre-paid bundles and save instantly. Check out our home cleaning savings on our Live Pricing & Real-Time Booking Pages."
}, {
  q: 'How can I pay you?',
  a: 'We accept VISA, Mastercard, American Express, and Discover through our convenient Live Pricing & Real-Time Booking Pages. Book online or call us. We also accept cash or cheque at the time of service.'
}];
export function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-431">
      <SEOHead title="Frequently Asked Questions | Smith & Jengs Cleaning Services" description="Find answers to common questions about Smith & Jengs cleaning services in Bicester and Oxfordshire. Learn about booking, pricing, eco-friendly products, and our satisfaction guarantee." canonical="https://smithandjengs.com/faq" data-id="element-432" />
      <FAQSchema faqs={faqs.map(f => ({
      question: f.q,
      answer: f.a
    }))} data-id="element-433" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'FAQ',
      url: 'https://smithandjengs.com/faq'
    }]} data-id="element-434" />
      <Navbar data-id="element-435" />

      <main className="flex-1" data-id="element-436">
        {/* Hero Section */}
        <section className="relative w-full h-[55vh] min-h-[420px] max-h-[600px] overflow-hidden" data-id="element-437">
          <div className="absolute inset-0" data-id="element-438">
            <img src="https://cdn.magicpatterns.com/uploads/k9prZ3TeuQMtKoLyUYkUo3/sj-website-faq-banner.png" alt="Warm interior with lamp and cabinet" className="w-full h-full object-cover object-right" loading="lazy" data-id="element-439" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe6]/85 via-[#f5efe6]/40 to-transparent" data-id="element-440" />
          </div>

          <div className="relative z-10 h-full flex items-end px-6 md:px-12 pb-12 pt-32" data-id="element-441">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="max-w-7xl mx-auto w-full" data-id="element-442">
              <h1 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide" data-id="element-443">
                <span className="text-6xl md:text-[7rem] lg:text-[9rem] block" data-id="element-444">
                  FREQUENTLY{' '}
                  <span className="font-serif-playfair italic font-normal normal-case text-6xl md:text-[6rem] lg:text-[8rem]" data-id="element-445">
                    Asked
                  </span>
                </span>
                <span className="text-6xl md:text-[7rem] lg:text-[9rem] block -mt-2 md:-mt-4" data-id="element-446">
                  QUESTIONS
                </span>
              </h1>
            </motion.div>
          </div>
        </section>

        {/* Accordion Section */}
        <section className="w-full bg-white py-16 md:py-24 px-6 md:px-12" data-id="element-447">
          <div className="max-w-3xl mx-auto" data-id="element-448">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} data-id="element-449">
              {faqs.map((faq, idx) => <div key={idx} className="border-b border-gray-200" data-id="element-450">
                  <button onClick={() => setOpenIndex(openIndex === idx ? null : idx)} className="w-full flex justify-between items-start text-left py-6 md:py-7 focus:outline-none" data-id="element-451">
                    <span className="text-base md:text-lg font-bold text-[#1a2744] pr-8 leading-snug" data-id="element-452">
                      {faq.q}
                    </span>
                    <ChevronDownIcon className={`w-5 h-5 text-[#1a2744] flex-shrink-0 mt-0.5 transition-transform duration-300 ${openIndex === idx ? 'rotate-180' : ''}`} data-id="element-453" />
                  </button>
                  <AnimatePresence data-id="element-454">
                    {openIndex === idx && faq.a && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-455">
                        <p className="text-gray-500 pb-6 text-sm md:text-base leading-relaxed max-w-xl" data-id="element-456">
                          {faq.a}
                        </p>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-457" />
    </div>;
}